import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
    GlobalAccountRoleType,
    GUID,
    OnboardingAccountRequest,
    OnboardingFileView,
    OnboardingStage,
    PersonOrg,
    PersonOrgOnboardingProfileSaveRequest,
    PersonOrgProfile,
    PersonOrgRelationType,
    PersonOrgSubtype,
    PersonOrgType,
    PortfolioBeneficiaries,
} from '@ndvr/customer-api';
import { Account } from '@ndvr/portfolio-api';

import { CustomerApi } from 'API';
import { API_ROOT } from 'env';
import { fetcher } from 'utils';
import { Toast } from 'widgets';

import { isReadingDocuments, toRelation } from './onboarding-model';

export const OnboardingQueryKeys = {
    // Under 'onboarding', which a Client Portal invite refreshes: the first invite moves the household to Invited
    status: (householdGUID: string) => ['onboarding', 'status', householdGUID] as const,
    files: (householdGUID: string) => ['globalAccount', householdGUID, 'onboarding', 'files'] as const,
    documentRead: (householdGUID: string) => ['globalAccount', householdGUID, 'onboarding', 'documentRead'] as const,
    // Outside 'globalAccount', whose invalidations (uploads, account saves) must not refetch bytes that never change
    fileContent: (onboardingFileGUID: string) => ['onboardingFileContent', onboardingFileGUID] as const,
    profiles: (householdGUID: string) => ['globalAccount', householdGUID, 'personOrgProfiles'] as const,
    links: (personOrgGUID: string) => ['personOrg', personOrgGUID, 'links'] as const,
    relations: (personOrgGUID: string) => ['personOrg', personOrgGUID, 'relations'] as const,
    accounts: (householdGUID: string) => ['globalAccount', householdGUID, 'onboarding', 'accounts'] as const,
};

const required = async <T>(call: Promise<[T | null, unknown?]>, message: string): Promise<T> => {
    const [response] = await call;
    if (response === null || response === undefined) throw new Error(message);
    return response;
};

// ---------------------------------------------------------------------------------------------------------------
// Stage
// ---------------------------------------------------------------------------------------------------------------

export const useOnboardingStatus = (householdGUID: string) =>
    useQuery({
        queryKey: OnboardingQueryKeys.status(householdGUID),
        queryFn: () => required(CustomerApi().getHubOnboardingStatus(householdGUID), 'Unable to load onboarding'),
    });

export const useChangeOnboardingStage = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (stage: OnboardingStage) =>
            required(CustomerApi({ hideError: true }).changeOnboardingStage(householdGUID, stage), 'No status'),
        onSuccess: (status) => {
            queryClient.setQueryData(OnboardingQueryKeys.status(householdGUID), status);
            void queryClient.invalidateQueries({ queryKey: ['onboarding', 'list'] });
            void queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID, 'commandCenterHouseholdProfile'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to change the stage'),
    });
};

// ---------------------------------------------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------------------------------------------

export const useSetOnboardingFeeSchedule = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (feeScheduleGUID: string) =>
            required(
                CustomerApi({ hideError: true }).setOnboardingFeeSchedule(householdGUID, feeScheduleGUID),
                'No status',
            ),
        onSuccess: (status) => queryClient.setQueryData(OnboardingQueryKeys.status(householdGUID), status),
        onError: (error) => Toast.forError(error, 'Failed to save the fee schedule'),
    });
};

// ---------------------------------------------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------------------------------------------

export const useOnboardingFiles = (householdGUID: string) =>
    useQuery({
        queryKey: OnboardingQueryKeys.files(householdGUID),
        queryFn: async () => (await CustomerApi().listHubOnboardingFiles(householdGUID))[0] ?? [],
    });

export const onboardingFileUrl = (householdGUID: string, file: OnboardingFileView) =>
    `${API_ROOT}/customer/hub/onboarding/household/${householdGUID}/files/${file.onboardingFileGUID}/content`;

/** Raw fetch: the generated client and `fetcher` turn the endpoint's `Content-Disposition` into a download. */
export const useOnboardingFileContent = (householdGUID: string, file: OnboardingFileView) =>
    useQuery({
        queryKey: OnboardingQueryKeys.fileContent(file.onboardingFileGUID),
        queryFn: async () => {
            const response = await fetch(onboardingFileUrl(householdGUID, file));
            if (!response.ok) throw new Error('Unable to load the file');
            return response.blob();
        },
        // A file's bytes never change; 5 minutes makes reopening instant while bounding the memory large PDFs hold
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });

const DOCUMENT_READ_POLL_MS = 3000;

/**
 * How far the reading of the documents is, polled while a reading runs. The files are loaded again with every poll, so
 * each shows how far it is, and once the reading ends, so are the people and accounts it built.
 */
export const useDocumentReadProgress = (householdGUID: string) => {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: OnboardingQueryKeys.documentRead(householdGUID),
        queryFn: () =>
            required(CustomerApi().getHubDocumentReadingProgress(householdGUID), 'Unable to load the reading progress'),
        refetchInterval: ({ state: { data } }) => (isReadingDocuments(data?.state) ? DOCUMENT_READ_POLL_MS : false),
    });
    const state = query.data?.state;
    const wasReading = useRef(false);
    useEffect(() => {
        const reading = isReadingDocuments(state);
        if (reading) {
            void queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.files(householdGUID) });
        } else if (wasReading.current) {
            void queryClient.invalidateQueries({ queryKey: ['globalAccount', householdGUID] });
            void queryClient.invalidateQueries({ queryKey: ['personOrg'] });
        }
        wasReading.current = reading;
    }, [queryClient, householdGUID, state, query.dataUpdatedAt]);
    return query;
};

/** Reads every file again and builds what is new; the backend refuses while a reading runs. */
export const useStartDocumentReading = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            required(
                CustomerApi({ hideError: true }).startHubDocumentReading(householdGUID),
                'The reading did not start',
            ),
        onSuccess: (progress) => {
            queryClient.setQueryData(OnboardingQueryKeys.documentRead(householdGUID), progress);
            return queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.files(householdGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to start reading the documents'),
    });
};

/** Every file of the household's onboarding in one zip, which the browser downloads. */
export const onboardingFilesArchiveUrl = (householdGUID: string) =>
    `${API_ROOT}/customer/hub/onboarding/household/${householdGUID}/files/archive`;

/**
 * Multipart by hand: the generated client sends the file as a query parameter. The backend accepts PDF, JPEG and PNG,
 * and only after the client allowed Claro to use their documents and while the client is still in progress.
 * TODO(backend): let Claro staff upload in the later stages too.
 */
export const useUploadOnboardingFile = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            // The shared fetcher, so errors are sanitized and edge rejections get their own guidance
            const [view] = await fetcher<OnboardingFileView>(
                `${API_ROOT}/customer/hub/onboarding/household/${householdGUID}/files`,
                { method: 'POST', body: formData },
            );
            return view;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.files(householdGUID) }),
        onError: (error) => Toast.forError(error, 'Failed to upload the file'),
    });
};

// ---------------------------------------------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------------------------------------------

export const useHouseholdProfiles = (householdGUID: string) =>
    useQuery({
        queryKey: OnboardingQueryKeys.profiles(householdGUID),
        queryFn: async () => (await CustomerApi().getPersonOrgProfilesForHousehold(householdGUID))[0] ?? [],
    });

const hasLinks = ({ personOrg }: PersonOrgProfile) =>
    personOrg.personOrgType === PersonOrgType.JOINT_PERSONS ||
    personOrg.personOrgType === PersonOrgType.TRUST ||
    personOrg.personOrgType === PersonOrgType.ORGANIZATION;

/** Every link of the household's joints and entities; one request each, fine at the few a household has. */
export const useHouseholdLinks = (profiles: PersonOrgProfile[]) =>
    useQueries({
        queries: profiles.filter(hasLinks).map(({ personOrgGUID }) => ({
            queryKey: OnboardingQueryKeys.links(personOrgGUID),
            queryFn: async () => (await CustomerApi().getPersonOrgLinksForParent(personOrgGUID))[0] ?? [],
        })),
        combine: (results) => ({
            data: results.flatMap((result) => result.data ?? []),
            isLoading: results.some((result) => result.isLoading),
        }),
    });

/** The relations of the person family relationships are told from (the primary client). */
export const useRelations = (personOrgGUID?: string) =>
    useQuery({
        queryKey: OnboardingQueryKeys.relations(personOrgGUID ?? ''),
        queryFn: async () => (await CustomerApi().getPersonOrgRelations(personOrgGUID!))[0] ?? [],
        enabled: !!personOrgGUID,
    });

export const useInvalidatePeople = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.profiles(householdGUID) }),
            queryClient.invalidateQueries({ queryKey: ['personOrg'] }),
        ]);
};

/** One save at a time per household: each replaces the person's whole profile. Cards show their own saving state. */
export const useSaveOnboardingProfile = (householdGUID: string) => {
    const invalidate = useInvalidatePeople(householdGUID);
    return useMutation({
        scope: { id: `OnboardingProfile-${householdGUID}` },
        mutationFn: ({
            personOrgGUID,
            request,
        }: {
            personOrgGUID: GUID<PersonOrg>;
            request: PersonOrgOnboardingProfileSaveRequest;
        }) => CustomerApi({ hideError: true }).saveOnboardingProfile(personOrgGUID, request),
        onSuccess: invalidate,
        onError: (error) => Toast.forError(error, 'Failed to save'),
    });
};

export interface NewPersonOrg {
    personOrgType: PersonOrgType;
    personOrgSubtype?: PersonOrgSubtype;
    /** The household role label that marks the person's kind (client, family, trusted professional role). */
    roleLabel?: string;
}

/** Creates an empty person on the household and labels their role; the caller fills in the profile. */
export const useCreateHouseholdPersonOrg = (householdGUID: string) => {
    const invalidate = useInvalidatePeople(householdGUID);
    return useMutation({
        mutationFn: async ({ personOrgType, personOrgSubtype, roleLabel }: NewPersonOrg) => {
            const api = CustomerApi({ hideError: true });
            const personOrgGUID = await required(
                api.createAndLinkPersonOrg(householdGUID, {
                    personOrgType,
                    personOrgSubtype,
                    defaultLanguageTag: 'en-us',
                }),
                'The person was not created',
            );
            if (roleLabel) {
                await api.addGlobalAccountPersonOrgRole(householdGUID, personOrgGUID, {
                    roleType: GlobalAccountRoleType.SECONDARY_USER,
                    roleLabel,
                });
            }
            return personOrgGUID;
        },
        onSuccess: invalidate,
        onError: (error) => Toast.forError(error, 'Failed to add'),
    });
};

export const useSetHouseholdRoleLabel = (householdGUID: string) => {
    const invalidate = useInvalidatePeople(householdGUID);
    return useMutation({
        mutationFn: ({ personOrgGUID, roleLabel }: { personOrgGUID: string; roleLabel: string }) =>
            CustomerApi({ hideError: true }).addGlobalAccountPersonOrgRole(householdGUID, personOrgGUID, {
                roleType: GlobalAccountRoleType.SECONDARY_USER,
                roleLabel,
            }),
        onSuccess: invalidate,
        onError: (error) => Toast.forError(error, 'Failed to save the role'),
    });
};

export const useRemoveHouseholdPerson = (householdGUID: string) => {
    const invalidate = useInvalidatePeople(householdGUID);
    return useMutation({
        mutationFn: (personOrgGUID: string) =>
            CustomerApi({ hideError: true }).unlinkPersonFromAccount(householdGUID, personOrgGUID),
        onSuccess: () => {
            Toast.success('Removed from the household');
            return invalidate();
        },
        onError: (error) => Toast.forError(error, 'Failed to remove'),
    });
};

/** Saves what `personOrgGUID` is to the anchor, or deletes the relationship when `relationType` is empty. */
export const useSetRelation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            anchorGUID,
            personOrgGUID,
            relationType,
        }: {
            anchorGUID: string;
            personOrgGUID: string;
            relationType?: PersonOrgRelationType;
        }) => {
            const api = CustomerApi({ hideError: true });
            return relationType
                ? api.savePersonOrgRelation(toRelation(anchorGUID, personOrgGUID, relationType))
                : api.deletePersonOrgRelationsBetween({
                      firstPersonOrgGUID: anchorGUID,
                      secondPersonOrgGUID: personOrgGUID,
                  });
        },
        onSuccess: (_, { anchorGUID }) =>
            queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.relations(anchorGUID) }),
        onError: (error) => Toast.forError(error, 'Failed to save the relationship'),
    });
};

// ---------------------------------------------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------------------------------------------

export const useOnboardingAccounts = (householdGUID: string) =>
    useQuery({
        queryKey: OnboardingQueryKeys.accounts(householdGUID),
        queryFn: async () => (await CustomerApi().listHubOnboardingAccounts(householdGUID))[0] ?? [],
    });

const useInvalidateAccounts = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: OnboardingQueryKeys.accounts(householdGUID) }),
            queryClient.invalidateQueries({ queryKey: ['globalAccount', householdGUID] }),
        ]);
};

/** One account write at a time per household: each update replaces every field of its account. */
const accountScope = (householdGUID: string) => ({ id: `OnboardingAccount-${householdGUID}` });

/** Creates the account; the backend creates the joint owner of a joint account. */
export const useCreateOnboardingAccount = (householdGUID: string) => {
    const invalidateAccounts = useInvalidateAccounts(householdGUID);
    const invalidatePeople = useInvalidatePeople(householdGUID);
    return useMutation({
        scope: accountScope(householdGUID),
        mutationFn: (request: OnboardingAccountRequest) =>
            required(
                CustomerApi({ hideError: true }).addHubOnboardingAccount(householdGUID, request),
                'The account was not created',
            ),
        onSuccess: () => {
            Toast.success('Account created');
            return Promise.all([invalidateAccounts(), invalidatePeople()]);
        },
        onError: (error) => Toast.forError(error, 'Failed to create the account'),
    });
};

/**
 * Saves the account, then its beneficiaries when they changed; a beneficiary list naming a new owner is dropped by the
 * account save, so the list is set after it.
 */
export const useSaveOnboardingAccount = (householdGUID: string) => {
    const invalidateAccounts = useInvalidateAccounts(householdGUID);
    const invalidatePeople = useInvalidatePeople(householdGUID);
    return useMutation({
        scope: accountScope(householdGUID),
        mutationFn: async ({
            accountGUID,
            request,
            beneficiaries,
        }: {
            accountGUID: GUID<Account>;
            request: OnboardingAccountRequest;
            beneficiaries?: PortfolioBeneficiaries;
        }) => {
            const api = CustomerApi({ hideError: true });
            await required(
                api.updateHubOnboardingAccount(householdGUID, accountGUID, request),
                'The account was not saved',
            );
            if (beneficiaries) {
                await required(
                    api.setHubOnboardingAccountBeneficiaries(householdGUID, accountGUID, beneficiaries),
                    'The beneficiaries were not saved',
                );
            }
        },
        onError: (error) => Toast.forError(error, 'Failed to save the account'),
        // Also after a failed beneficiary save, so the card shows the account update that went through
        onSettled: () => Promise.all([invalidateAccounts(), invalidatePeople()]),
    });
};

export const useDeleteOnboardingAccount = (householdGUID: string) => {
    const invalidate = useInvalidateAccounts(householdGUID);
    return useMutation({
        scope: accountScope(householdGUID),
        mutationFn: (accountGUID: GUID<Account>) =>
            CustomerApi({ hideError: true }).removeHubOnboardingAccount(householdGUID, accountGUID),
        onSuccess: () => {
            Toast.success('Account deleted');
            return invalidate();
        },
        onError: (error) => Toast.forError(error, 'Failed to delete the account'),
    });
};
